import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import {
  AlertTriangle, BookOpen, CheckCircle2, ChevronRight,
  Clock, FileText, Lightbulb, Sparkles, Target, TrendingUp,
  Users, X, Zap,
} from "lucide-react";
import { useDemoStore } from "@/store/useDemoStore";

// ─── Tipos ────────────────────────────────────────────────────────────────────

type Tone = "teal" | "navy" | "amber" | "red" | "green";

interface Insight {
  icon: React.ReactNode;
  title: string;
  body: string;
  metric?: string;
  metricLabel?: string;
  tone: Tone;
  action?: string;
}

// ─── Colores por tono ─────────────────────────────────────────────────────────

const TONE_STYLES: Record<Tone, { border: string; bg: string; badge: string; text: string }> = {
  teal:  { border: "var(--cc-teal)",  bg: "rgba(8,145,178,0.05)",  badge: "#0891b2", text: "white" },
  navy:  { border: "var(--cc-navy)",  bg: "rgba(15,23,42,0.04)",   badge: "#0f172a", text: "white" },
  amber: { border: "#d97706",          bg: "rgba(217,119,6,0.06)",  badge: "#d97706", text: "white" },
  red:   { border: "#dc2626",          bg: "rgba(220,38,38,0.05)",  badge: "#dc2626", text: "white" },
  green: { border: "#16a34a",          bg: "rgba(22,163,74,0.05)",  badge: "#16a34a", text: "white" },
};

// ─── Mapa de insights por rol y ruta ──────────────────────────────────────────

type RoleInsights = Record<string, Insight[]>;
type InsightsMap = Record<string, RoleInsights>;

const INSIGHTS: InsightsMap = {
  // ── ADMIN ──────────────────────────────────────────────────────────────────
  admin: {
    "/admin": [
      {
        icon: <AlertTriangle size={15} />, tone: "amber",
        title: "Sgto. 2° Diego Ortega y Cb. Sofía Reyes con avance crítico",
        body: "Diego Ortega lleva solo 12% de avance y Sofía Reyes 8% en la Generación 2026-A. Ambos sin actividad desde hace más de 5 días.",
        metric: "2", metricLabel: "en riesgo",
        action: "Ver candidatos",
      },
      {
        icon: <CheckCircle2 size={15} />, tone: "green",
        title: "Sgto. 1° Jorge Ramírez Pacheco listo para dictamen",
        body: "92% de avance, expediente completo y prácticas aprobadas. Dr. Ernesto Vargas Ibañez tiene el expediente asignado. Es el candidato más avanzado de Gen 2026-A.",
        metric: "92%", metricLabel: "avance",
      },
      {
        icon: <Users size={15} />, tone: "teal",
        title: "Generación 2026-A: 5 candidatos aún en proceso",
        body: "Cap. Manuel Treviño coordina 8 candidatos. Solo Ramírez y Patricia Vázquez han concluido. Los 5 restantes están entre 22% y 78% de avance.",
        metric: "5", metricLabel: "en proceso",
        action: "Ver generación",
      },
      {
        icon: <Lightbulb size={15} />, tone: "navy",
        title: "Generación 2026-B inicia en julio — prepara instrumentos",
        body: "Sgto. 1° Ramón Fuentes Olmos tiene 4 candidatos inscritos. La sesión de inducción está calendarizada para el 2 de julio en el Patio de revisión.",
      },
    ],
    "/admin/estandares": [
      {
        icon: <BookOpen size={15} />, tone: "teal",
        title: "EC1781 concentra 24 candidatos activos",
        body: "Es el estándar más demandado del ciclo con 187 certificados históricos. Asegura que la Ruta formativa v3 esté publicada antes del cierre de Gen 2026-A.",
        metric: "24", metricLabel: "candidatos activos",
      },
      {
        icon: <TrendingUp size={15} />, tone: "green",
        title: "EC0217 acumula 1,453 certificados emitidos",
        body: "Es el estándar con mayor madurez institucional. Su tasa de certificación histórica es referencia para calibrar los criterios de dictamen de EC1781.",
        metric: "1,453", metricLabel: "certificados",
      },
      {
        icon: <AlertTriangle size={15} />, tone: "amber",
        title: "Hay un estándar en borrador sin candidatos",
        body: "El estándar de Prestación de servicios a clientes tiene 0 candidatos activos y 0 certificados. Considera publicarlo o pausar su desarrollo hasta tener demanda.",
        action: "Revisar borrador",
      },
      {
        icon: <Zap size={15} />, tone: "navy",
        title: "Tip: configura reglas de dictamen antes de cada nueva generación",
        body: "Estándares sin reglas de dictamen completas generan intervención manual. EC1781 ya tiene 8 reglas activas — replica ese modelo en EC0217.",
      },
    ],
    "/admin/rutas-formativas": [
      {
        icon: <TrendingUp size={15} />, tone: "green",
        title: "EC1781 v3 es la más robusta del catálogo",
        body: "Rodrigo Fuentes publicó la Revisión integral 2026 el 28 de mayo con 14 módulos y 7 bloques. De v1 a v3 se añadieron 5 módulos en 5 meses.",
        metric: "v3", metricLabel: "vigente",
      },
      {
        icon: <Users size={15} />, tone: "teal",
        title: "Dos instructores distintos en EC0217",
        body: "Carlos Mendoza publicó la versión base y Ma. Luisa Vargas actualizó en abril 2026. Verifica que el traspaso fue formal y que los 4 candidatos activos recibieron la nueva versión.",
        action: "Ver historial",
      },
      {
        icon: <CheckCircle2 size={15} />, tone: "green",
        title: "EC1100 tiene ruta publicada y candidatos asignados",
        body: "Jorge Palomino publicó en mayo 2026 con 6 módulos. Con 8 candidatos activos es el grupo de mayor tamaño por instructor en el catálogo.",
        metric: "8", metricLabel: "candidatos",
      },
      {
        icon: <Lightbulb size={15} />, tone: "navy",
        title: "Patrón ideal: iterar la ruta cada 6-8 semanas",
        body: "EC1781 tiene 3 versiones en 5 meses — el ritmo de mejora continua más alto del catálogo. Las generaciones posteriores se benefician de cada actualización.",
      },
    ],
    "/admin/grupos": [
      {
        icon: <AlertTriangle size={15} />, tone: "amber",
        title: "Gen 2026-A: 3 candidatos con avance menor al 25%",
        body: "Cb. Andrés Martínez (22%), Sgto. 2° Diego Ortega (12%) y Cb. Sofía Reyes (8%) están muy por debajo del ritmo esperado. Cap. Manuel Treviño los tiene asignados.",
        metric: "3", metricLabel: "rezagados",
        action: "Ver generación",
      },
      {
        icon: <CheckCircle2 size={15} />, tone: "green",
        title: "Gen 2026-A tiene 2 candidatos ya certificados",
        body: "Sgto. 1° Patricia Vázquez León y Sgto. 1° Fernando López Rivera completaron al 100%. Son los mejores perfiles para testimonios institucionales.",
        metric: "2", metricLabel: "certificados",
      },
      {
        icon: <Clock size={15} />, tone: "teal",
        title: "Gen 2026-B programada: inducción el 2-jul-2026",
        body: "Sgto. 1° Ramón Fuentes Olmos tiene calendarizadas 4 sesiones para Gen 2026-B. Verifica que los 4 candidatos (Fernando López, Karla Jiménez, Héctor Morales, Beatriz Castillo) tengan acceso activo.",
        metric: "4", metricLabel: "candidatos",
      },
    ],
    "/admin/candidatos": [
      {
        icon: <CheckCircle2 size={15} />, tone: "green",
        title: "Sgto. 1° Jorge Ramírez Pacheco — expediente en dictamen",
        body: "El candidato con mayor avance de Gen 2026-A (92%) ya tiene expediente completo. Dr. Ernesto Vargas Ibañez está asignado como dictaminador.",
        metric: "92%", metricLabel: "avance",
      },
      {
        icon: <AlertTriangle size={15} />, tone: "red",
        title: "Cb. Sofía Reyes (8%) y Sgto. Diego Ortega (12%) en riesgo",
        body: "Son los candidatos con menor avance de Gen 2026-A. Ambos inscrito sin evaluaciones prácticas completadas. Requieren contacto directo del instructor.",
        action: "Ver perfiles",
      },
      {
        icon: <TrendingUp size={15} />, tone: "teal",
        title: "Cb. Mariana Hernández al 78% — próxima a evaluación final",
        body: "Está en estado 'en evaluación', igual que Sgto. Luis Aguilar (65%). Ambos pueden cerrar su proceso en las próximas 2 semanas si mantienen el ritmo.",
        metric: "78%", metricLabel: "avance Hernández",
      },
      {
        icon: <FileText size={15} />, tone: "navy",
        title: "Cb. Karla Jiménez: resultado 'todavía no competente'",
        body: "Completó el proceso al 100% pero no alcanzó el umbral de dictamen. Tiene derecho a un segundo proceso según las reglas EC1781. Verifica con Dr. Vargas.",
      },
    ],
    "_default": [
      {
        icon: <Sparkles size={15} />, tone: "teal",
        title: "EC1781 activo con 2 generaciones en marcha",
        body: "La Generación 2026-A está en curso con Cap. Manuel Treviño. La Generación 2026-B inicia en julio con Sgto. 1° Ramón Fuentes. 24 candidatos activos en total.",
      },
      {
        icon: <Lightbulb size={15} />, tone: "navy",
        title: "Tip: asigna dictaminadores con 7 días de anticipación",
        body: "Dr. Ernesto Vargas Ibañez ya tiene asignado el expediente de Ramírez Pacheco. Replica ese flujo para Mariana Hernández y Luis Aguilar cuando completen.",
      },
    ],
  },

  // ── INSTRUCTOR ─────────────────────────────────────────────────────────────
  instructor: {
    "/instructor": [
      {
        icon: <AlertTriangle size={15} />, tone: "amber",
        title: "Sgto. 2° Héctor Morales y Cb. Beatriz Castillo al 0%",
        body: "Ambos candidatos de Gen 2026-B tienen 0% de avance y sin actividad registrada. La inducción del grupo está calendarizada para el 2 de julio.",
        metric: "2", metricLabel: "sin avance",
      },
      {
        icon: <CheckCircle2 size={15} />, tone: "green",
        title: "Sgto. 1° Fernando López Rivera ya certificado",
        body: "Completó al 100% y obtuvo dictamen favorable. Es el candidato con más experiencia del grupo (18 años en motocicleta). Ideal para mentoría de pares.",
        metric: "100%", metricLabel: "certificado",
      },
      {
        icon: <Clock size={15} />, tone: "teal",
        title: "Primera sesión de Gen 2026-B: 2 de julio, 08:00h",
        body: "Sesión de inducción en el Patio de revisión, 180 minutos. Tienes 4 candidatos confirmados: Fernando López, Karla Jiménez, Héctor Morales y Beatriz Castillo.",
        metric: "4", metricLabel: "candidatos",
      },
    ],
    "/instructor/practicas": [
      {
        icon: <Target size={15} />, tone: "teal",
        title: "5 prácticas calendarizadas para Gen 2026-A",
        body: "Pista 1 y Circuito exterior con Cap. Manuel Treviño. Las prácticas cubren: Revisión preoperativa (E5637), Equilibrio, Frenado, Maniobras evasivas y Slalom (E5638).",
      },
      {
        icon: <Clock size={15} />, tone: "amber",
        title: "Práctica de Slalom calendarizada para el 11-jun-2026",
        body: "Es la práctica más larga: 300 min en Circuito exterior. El evaluador Cap. Manuel Treviño Pardo ya está asignado. Confirma asistencia de todos los candidatos.",
        metric: "300min", metricLabel: "duración",
      },
      {
        icon: <Lightbulb size={15} />, tone: "navy",
        title: "Gen 2026-B: 4 sesiones desde el 2-jul-2026",
        body: "Pista 1 y Circuito exterior. Las sesiones de Gen 2026-B tienen la misma estructura que Gen 2026-A. Aprovecha los aprendizajes del grupo anterior.",
      },
    ],
    "/instructor/material": [
      {
        icon: <TrendingUp size={15} />, tone: "green",
        title: "La Ruta formativa EC1781 v3 tiene 14 módulos activos",
        body: "Revisión integral publicada el 28 de mayo 2026 con 7 bloques. Los 8 candidatos de Gen 2026-A tienen acceso a la versión vigente.",
        metric: "14", metricLabel: "módulos",
      },
      {
        icon: <Lightbulb size={15} />, tone: "teal",
        title: "Bloque de Seguridad Operacional es el más crítico",
        body: "El 60% de preguntas del examen teórico provienen de este bloque. Asegúrate de que los módulos de frenado y maniobras evasivas tengan contenido audiovisual completo.",
      },
      {
        icon: <Zap size={15} />, tone: "navy",
        title: "Publicar cambios llega a 8 candidatos de Gen 2026-A",
        body: "Cualquier actualización que publiques se refleja inmediatamente en la ruta de los candidatos activos. Gen 2026-B recibirá la versión vigente al iniciar en julio.",
        metric: "8", metricLabel: "candidatos activos",
        action: "Publicar ruta",
      },
    ],
    "/instructor/grupos": [
      {
        icon: <Users size={15} />, tone: "teal",
        title: "Gen 2026-B: 4 candidatos, inicia julio 2026",
        body: "Sgto. 1° Fernando López (certificado), Cb. Karla Jiménez (no competente), Sgto. 2° Héctor Morales y Cb. Beatriz Castillo (inscritos). Sede: Pato de revisión y Pista 1.",
        metric: "4", metricLabel: "candidatos",
      },
      {
        icon: <Lightbulb size={15} />, tone: "navy",
        title: "Cb. Karla Jiménez tiene segunda oportunidad",
        body: "Obtuvo 'todavía no competente' en Gen 2026-B anterior. Si está reincorporada, requiere atención especial en los criterios de E5638 donde tuvo dificultades.",
        action: "Ver expediente",
      },
    ],
    "_default": [
      {
        icon: <Sparkles size={15} />, tone: "teal",
        title: "Gen 2026-B lista para iniciar en julio",
        body: "Tienes 4 candidatos asignados y 4 sesiones calendarizadas. La estructura replica exitosamente el modelo de Gen 2026-A con Cap. Manuel Treviño.",
      },
    ],
  },

  // ── ALUMNO / CANDIDATO ─────────────────────────────────────────────────────
  alumno: {
    "/alumno": [
      {
        icon: <CheckCircle2 size={15} />, tone: "green",
        title: "Tu expediente está en dictamen con Dr. Vargas Ibañez",
        body: "Sgto. 1° Jorge Ramírez Pacheco, tu expediente completo fue enviado al Dr. Ernesto Vargas Ibañez. Eres el candidato con mayor avance de la Generación 2026-A.",
        metric: "92%", metricLabel: "tu avance",
      },
      {
        icon: <Clock size={15} />, tone: "teal",
        title: "Eres el candidato más avanzado de Gen 2026-A",
        body: "Tu progreso del 92% supera a Cb. Mariana Hernández (78%) y Sgto. Luis Aguilar (65%). Solo Patricia Vázquez y tú han llegado a dictamen en esta generación.",
      },
      {
        icon: <Lightbulb size={15} />, tone: "navy",
        title: "12 años de experiencia son tu fortaleza en E5638",
        body: "Tu trayectoria en la División Caminos de la Guardia Nacional te da ventaja en los criterios de conducción operativa. El evaluador Cap. Treviño lo considerará en la evaluación.",
      },
    ],
    "/alumno/ruta": [
      {
        icon: <TrendingUp size={15} />, tone: "green",
        title: "Ruta formativa EC1781 v3 — Revisión integral 2026",
        body: "Estás en la versión más actualizada: 7 bloques, 14 módulos. Publicada el 28 de mayo 2026. Tu progreso del 92% te coloca en la recta final.",
        metric: "v3", metricLabel: "versión vigente",
      },
      {
        icon: <Target size={15} />, tone: "teal",
        title: "Bloque E5638 cubre conducción en circuito",
        body: "Los módulos de Equilibrio, Frenado y Maniobras evasivas son los más evaluados en práctica. Con 12 años de experiencia operativa tienes base sólida.",
      },
      {
        icon: <Lightbulb size={15} />, tone: "navy",
        title: "Simulador preoperativo: practica antes de la evaluación",
        body: "El simulador de revisión preoperativa (E5637) replica exactamente la lista de cotejo que usa el evaluador en pista. Repásalo antes de tu próxima sesión.",
        action: "Ir al simulador",
      },
    ],
    "/alumno/expediente": [
      {
        icon: <CheckCircle2 size={15} />, tone: "green",
        title: "Expediente enviado a dictamen — Dr. Ernesto Vargas Ibañez",
        body: "Tu expediente como Sgto. 1° Jorge Ramírez Pacheco (matrícula GN-204815) está completo y en revisión. El dictaminador tiene un plazo estándar de 5 días hábiles.",
      },
      {
        icon: <FileText size={15} />, tone: "teal",
        title: "Unidad: Guardia Nacional — División Caminos",
        body: "Tus 12 años de experiencia operativa en motocicleta están respaldados en el expediente. Asegúrate de que la constancia de servicio esté actualizada al mes en curso.",
      },
      {
        icon: <Lightbulb size={15} />, tone: "navy",
        title: "Examen médico: verifica vigencia",
        body: "Una de las reglas de dictamen de EC1781 requiere examen médico vigente apto para servicio operativo. Si el tuyo tiene más de 6 meses, renuévalo antes del dictamen.",
      },
    ],
    "/alumno/evidencias": [
      {
        icon: <Target size={15} />, tone: "teal",
        title: "EC1781 requiere 6 evidencias — regla RE bloqueante",
        body: "Las 6 evidencias de desempeño son requisito bloqueante según las reglas de dictamen EC1781. Sin ellas, el Dr. Vargas Ibañez no puede emitir dictamen favorable.",
        metric: "6", metricLabel: "evidencias requeridas",
      },
      {
        icon: <Lightbulb size={15} />, tone: "navy",
        title: "La evidencia de video integral es clave para EC1781",
        body: "EC1781 incluye una regla de advertencia sobre el video del recorrido completo. Aunque no es bloqueante, el dictaminador la considera para el resultado final.",
      },
      {
        icon: <CheckCircle2 size={15} />, tone: "green",
        title: "Tu instructor valida las evidencias antes del dictamen",
        body: "Cap. Manuel Treviño Pardo revisa y aprueba cada evidencia antes de que lleguen a Dr. Ernesto Vargas. Las evidencias validadas tienen mayor peso en el dictamen.",
      },
    ],
    "/alumno/historial": [
      {
        icon: <TrendingUp size={15} />, tone: "green",
        title: "Última actividad: 26 de mayo 2026 a las 14:22",
        body: "Eres el candidato con actividad más reciente de la Generación 2026-A. Tu constancia es notoria frente a candidatos como Diego Ortega (última actividad: 22 mayo).",
        metric: "26-may", metricLabel: "última sesión",
      },
      {
        icon: <FileText size={15} />, tone: "navy",
        title: "De inscrito a en-dictamen en un ciclo",
        body: "Tu trayectoria en Gen 2026-A muestra progresión: inscrito → en-evaluación → en-dictamen. Solo Patricia Vázquez y tú han llegado a esta etapa en el grupo.",
      },
    ],
    "_default": [
      {
        icon: <Sparkles size={15} />, tone: "teal",
        title: "Sgto. 1° Jorge Ramírez — 92% de avance",
        body: "Eres el candidato con mayor progreso de la Generación 2026-A. Tu expediente está con Dr. Ernesto Vargas Ibañez para dictamen.",
      },
    ],
  },
};

// ─── Obtiene insights para la ruta y rol actuales ─────────────────────────────

function getInsights(role: string, path: string): Insight[] {
  const roleMap = INSIGHTS[role] ?? INSIGHTS.admin;
  // Busca coincidencia exacta primero, luego por prefijo, luego default
  if (roleMap[path]) return roleMap[path];
  const prefixMatch = Object.keys(roleMap)
    .filter((k) => k !== "_default" && path.startsWith(k))
    .sort((a, b) => b.length - a.length)[0];
  if (prefixMatch) return roleMap[prefixMatch];
  return roleMap["_default"] ?? [];
}

// ─── Nombre legible de la ruta ────────────────────────────────────────────────

const PATH_LABELS: Record<string, string> = {
  "/admin": "Dashboard · Admin",
  "/admin/estandares": "Estándares",
  "/admin/rutas-formativas": "Rutas formativas",
  "/admin/grupos": "Grupos y generaciones",
  "/admin/candidatos": "Candidatos",
  "/admin/usuarios": "Usuarios y roles",
  "/instructor": "Dashboard · Instructor",
  "/instructor/practicas": "Prácticas",
  "/instructor/material": "Ruta formativa",
  "/instructor/grupos": "Mis grupos",
  "/alumno": "Mi progreso",
  "/alumno/ruta": "Ruta formativa",
  "/alumno/expediente": "Mi expediente",
  "/alumno/evidencias": "Mis evidencias",
  "/alumno/historial": "Historial",
};

function getPathLabel(path: string) {
  if (PATH_LABELS[path]) return PATH_LABELS[path];
  const prefix = Object.keys(PATH_LABELS)
    .filter((k) => path.startsWith(k))
    .sort((a, b) => b.length - a.length)[0];
  return prefix ? PATH_LABELS[prefix] : "Vista actual";
}

// ─── Tarjeta de insight ───────────────────────────────────────────────────────

function InsightCard({ insight, delay }: { insight: Insight; delay: number }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  const s = TONE_STYLES[insight.tone];
  return (
    <div style={{
      borderLeft: `3px solid ${s.border}`,
      background: s.bg,
      borderRadius: "0 10px 10px 0",
      padding: "12px 14px",
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(8px)",
      transition: "opacity 0.35s ease, transform 0.35s ease",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 8, flexShrink: 0,
          background: s.badge, color: s.text,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {insight.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--cc-navy)", lineHeight: 1.3 }}>
              {insight.title}
            </span>
            {insight.metric && (
              <span style={{
                fontSize: 11, fontWeight: 800, padding: "1px 7px", borderRadius: 20,
                background: s.badge, color: s.text, whiteSpace: "nowrap",
              }}>
                {insight.metric} {insight.metricLabel}
              </span>
            )}
          </div>
          <p style={{ fontSize: 12, color: "var(--cc-gray-600)", lineHeight: 1.55, margin: 0 }}>
            {insight.body}
          </p>
          {insight.action && (
            <button style={{
              marginTop: 8, display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: 11, fontWeight: 700, color: s.badge,
              background: "none", border: "none", cursor: "pointer", padding: 0,
            }}>
              {insight.action} <ChevronRight size={11} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Drawer principal ─────────────────────────────────────────────────────────

interface IaContextDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function IaContextDrawer({ open, onClose }: IaContextDrawerProps) {
  const role = useDemoStore((s) => s.activeRole);
  const [location] = useLocation();
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState<Insight[]>([]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setInsights([]);
    const t = setTimeout(() => {
      setInsights(getInsights(role, location));
      setLoading(false);
    }, 1100);
    return () => clearTimeout(t);
  }, [open, role, location]);

  const pageLabel = getPathLabel(location);

  const roleLabels: Record<string, string> = {
    admin: "Admin académico",
    instructor: "Instructor",
    alumno: "Candidato",
    evaluador: "Evaluador",
    dictaminador: "Dictaminador",
    auditor: "Auditor",
  };

  return (
    <>
      {/* Overlay */}
      {open && (
        <div
          onClick={onClose}
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)",
            zIndex: 999, backdropFilter: "blur(2px)",
          }}
        />
      )}

      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: 400, background: "white",
        boxShadow: "-8px 0 32px rgba(0,0,0,0.12)",
        zIndex: 1000, display: "flex", flexDirection: "column",
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
      }}>
        {/* Header */}
        <div style={{
          padding: "18px 20px 14px",
          borderBottom: "1px solid var(--cc-gray-100)",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                width: 30, height: 30, borderRadius: 8,
                background: "linear-gradient(135deg, var(--cc-navy) 0%, var(--cc-teal) 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Sparkles size={14} color="white" />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }}>Agente IA</div>
                <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{roleLabels[role] ?? role}</div>
              </div>
            </div>
            <button onClick={onClose} style={{
              width: 28, height: 28, borderRadius: 6,
              border: "1px solid var(--cc-gray-200)", background: "white",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer",
            }}>
              <X size={14} color="var(--cc-gray-500)" />
            </button>
          </div>
          <div style={{
            marginTop: 10, padding: "7px 12px", borderRadius: 8,
            background: "rgba(8,145,178,0.06)", border: "1px solid rgba(8,145,178,0.15)",
            fontSize: 12, color: "var(--cc-teal)", fontWeight: 600,
          }}>
            Insights para: <span style={{ color: "var(--cc-navy)" }}>{pageLabel}</span>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 8 }}>
              {/* Skeleton loader */}
              {[1, 2, 3].map((i) => (
                <div key={i} style={{
                  borderLeft: "3px solid var(--cc-gray-200)",
                  background: "var(--cc-gray-50)", borderRadius: "0 10px 10px 0",
                  padding: "12px 14px",
                }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "var(--cc-gray-200)", flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ height: 12, background: "var(--cc-gray-200)", borderRadius: 4, width: "70%", marginBottom: 8,
                        animation: "pulse 1.2s ease-in-out infinite" }} />
                      <div style={{ height: 10, background: "var(--cc-gray-100)", borderRadius: 4, width: "100%", marginBottom: 4 }} />
                      <div style={{ height: 10, background: "var(--cc-gray-100)", borderRadius: 4, width: "85%" }} />
                    </div>
                  </div>
                </div>
              ))}
              <div style={{ textAlign: "center", marginTop: 8 }}>
                <span style={{ fontSize: 12, color: "var(--cc-gray-400)" }}>Analizando contexto…</span>
              </div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--cc-gray-400)" }}>
                {insights.length} insights detectados
              </div>
              {insights.map((ins, i) => (
                <InsightCard key={i} insight={ins} delay={i * 120} />
              ))}
              <div style={{
                marginTop: 8, padding: "12px 14px", borderRadius: 10,
                background: "var(--cc-gray-50)", border: "1px solid var(--cc-gray-100)",
                fontSize: 11, color: "var(--cc-gray-400)", textAlign: "center", lineHeight: 1.6,
              }}>
                Los insights se actualizan cada vez que navegas a una nueva sección.
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </>
  );
}
